<template>
  <div>
    <vue-element-loading :active="busy" :is-full-screen="true" background-color="#FFFFFF" color="#15803d" spinner="spinner" />

    <!-- Create -->
    <v-dialog v-model="createDialog" max-width="660" persistent scrollable>
      <v-card class="dialog-card">
        <div class="dialog-head">
          <div class="dialog-head-title">Create user account</div>
          <div class="dialog-head-subtitle">Fields marked * are required. An active account can sign in straight away.</div>
        </div>
        <v-card-text class="uf-body">
          <div class="uf-grid">
            <div class="uf-field uf-field--full">
              <label class="uf-label" for="uf-create-role">Designation / tier *</label>
              <v-autocomplete
                id="uf-create-role"
                v-model="createForm.uType"
                :items="roleItems"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-badge-account-horizontal-outline"
                @change="onCreateRoleChange"
              />
            </div>

            <div v-if="needsCircle(createForm.uType)" class="uf-field" :class="{ 'uf-field--full': !needsDivision(createForm.uType) }">
              <label class="uf-label" for="uf-create-circle">Parent circle *</label>
              <v-autocomplete
                id="uf-create-circle"
                v-model="createForm.circleId"
                :items="circles"
                item-text="officeName"
                item-value="_id"
                outlined
                dense
                hide-details
                placeholder="Select circle"
                prepend-inner-icon="mdi-compass-outline"
                @change="onCreateCircleChange"
              />
            </div>

            <div v-if="needsDivision(createForm.uType)" class="uf-field">
              <label class="uf-label" for="uf-create-division">Assigned division *</label>
              <v-autocomplete
                id="uf-create-division"
                v-model="createForm.divisionId"
                :items="filteredCreateDivisions"
                item-text="officeName"
                item-value="_id"
                outlined
                dense
                hide-details
                :disabled="!createForm.circleId"
                :placeholder="createForm.circleId ? 'Select division' : 'Select a circle first'"
                prepend-inner-icon="mdi-office-building-outline"
                @change="onCreateDivisionChange"
              />
            </div>

            <div v-if="createHierarchyPreview" class="uf-field--full uf-preview">
              <v-icon size="16">mdi-link-variant</v-icon>
              <span>Assigned jurisdiction: <strong>{{ createHierarchyPreview }}</strong></span>
            </div>

            <div class="uf-field">
              <label class="uf-label" for="uf-create-username">Username *</label>
              <v-text-field id="uf-create-username" v-model="createForm.username" placeholder="e.g. dfonilambur" outlined dense hide-details />
            </div>
            <div class="uf-field">
              <label class="uf-label" for="uf-create-office">Full name / office name *</label>
              <v-text-field id="uf-create-office" v-model="createForm.officeName" placeholder="e.g. Nilambur South Division" outlined dense hide-details />
            </div>
            <div class="uf-field">
              <label class="uf-label" for="uf-create-email">Email ID *</label>
              <v-text-field id="uf-create-email" v-model="createForm.emailId" type="email" placeholder="e.g. dfo-nilambur@kerala.gov.in" outlined dense hide-details />
            </div>
            <div class="uf-field">
              <label class="uf-label" for="uf-create-mobile">Mobile number *</label>
              <v-text-field id="uf-create-mobile" v-model="createForm.mobNumber" placeholder="10-digit mobile number" outlined dense hide-details />
            </div>
            <div class="uf-field">
              <label class="uf-label" for="uf-create-password">Password *</label>
              <v-text-field
                id="uf-create-password"
                v-model="createForm.password"
                :type="showCreatePassword ? 'text' : 'password'"
                placeholder="Set initial password"
                outlined
                dense
                hide-details
                :append-icon="showCreatePassword ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append="showCreatePassword = !showCreatePassword"
              />
            </div>
            <div class="uf-field">
              <label class="uf-label" for="uf-create-status">Account status</label>
              <v-select id="uf-create-status" v-model="createForm.status" :items="statusItems" outlined dense hide-details :menu-props="{ offsetY: true }" />
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="px-5 pb-5">
          <v-spacer />
          <v-btn depressed class="btn-act btn-act--neutral" @click="createDialog = false">Cancel</v-btn>
          <v-btn depressed class="btn-act btn-act--primary" :disabled="busy" @click="submitCreateUser">
            <v-icon left small>mdi-check</v-icon>Create account
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit -->
    <v-dialog v-model="editDialog" max-width="660" persistent scrollable>
      <v-card class="dialog-card">
        <div class="dialog-head">
          <div class="dialog-head-title">Edit user details</div>
          <div class="dialog-head-subtitle">{{ editForm.username }}</div>
        </div>
        <v-card-text class="uf-body">
          <div class="uf-grid">
            <div class="uf-field uf-field--full">
              <label class="uf-label" for="uf-edit-role">Designation / tier *</label>
              <v-autocomplete
                id="uf-edit-role"
                v-model="editForm.uType"
                :items="roleItems"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-badge-account-horizontal-outline"
                @change="onEditRoleChange"
              />
            </div>

            <div v-if="needsCircle(editForm.uType)" class="uf-field" :class="{ 'uf-field--full': !needsDivision(editForm.uType) }">
              <label class="uf-label" for="uf-edit-circle">Parent circle *</label>
              <v-autocomplete
                id="uf-edit-circle"
                v-model="editForm.circleId"
                :items="circles"
                item-text="officeName"
                item-value="_id"
                outlined
                dense
                hide-details
                placeholder="Select circle"
                prepend-inner-icon="mdi-compass-outline"
                @change="onEditCircleChange"
              />
            </div>

            <div v-if="needsDivision(editForm.uType)" class="uf-field">
              <label class="uf-label" for="uf-edit-division">Assigned division *</label>
              <v-autocomplete
                id="uf-edit-division"
                v-model="editForm.divisionId"
                :items="filteredEditDivisions"
                item-text="officeName"
                item-value="_id"
                outlined
                dense
                hide-details
                :disabled="!editForm.circleId"
                :placeholder="editForm.circleId ? 'Select division' : 'Select a circle first'"
                prepend-inner-icon="mdi-office-building-outline"
              />
            </div>

            <div v-if="editHierarchyPreview" class="uf-field--full uf-preview">
              <v-icon size="16">mdi-link-variant</v-icon>
              <span>Assigned jurisdiction: <strong>{{ editHierarchyPreview }}</strong></span>
            </div>

            <div class="uf-field">
              <label class="uf-label" for="uf-edit-username">Username *</label>
              <v-text-field id="uf-edit-username" v-model="editForm.username" outlined dense hide-details />
            </div>
            <div class="uf-field">
              <label class="uf-label" for="uf-edit-office">Full name / office name</label>
              <v-text-field id="uf-edit-office" v-model="editForm.officeName" outlined dense hide-details />
            </div>
            <div class="uf-field">
              <label class="uf-label" for="uf-edit-email">Email ID *</label>
              <v-text-field id="uf-edit-email" v-model="editForm.emailId" type="email" outlined dense hide-details />
            </div>
            <div class="uf-field">
              <label class="uf-label" for="uf-edit-mobile">Mobile number *</label>
              <v-text-field id="uf-edit-mobile" v-model="editForm.mobNumber" outlined dense hide-details />
            </div>
            <div class="uf-field">
              <label class="uf-label" for="uf-edit-status">Account status</label>
              <v-select id="uf-edit-status" v-model="editForm.status" :items="statusItems" outlined dense hide-details :menu-props="{ offsetY: true }" />
            </div>
            <div v-if="Number(editForm.uType) === 774" class="uf-field">
              <label class="uf-label" for="uf-edit-code">Division code</label>
              <v-text-field id="uf-edit-code" v-model="editForm.code" placeholder="eTreasury office code" outlined dense hide-details />
              <div class="uf-hint">Leave blank to keep the current code.</div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="px-5 pb-5">
          <v-spacer />
          <v-btn depressed class="btn-act btn-act--neutral" @click="editDialog = false">Cancel</v-btn>
          <v-btn depressed class="btn-act btn-act--primary" :disabled="busy" @click="submitEditUser">
            <v-icon left small>mdi-content-save-outline</v-icon>Save changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reset password -->
    <v-dialog v-model="passwordDialog" max-width="500" persistent>
      <v-card class="dialog-card">
        <div class="dialog-head">
          <div class="dialog-head-title">Reset password</div>
          <div class="dialog-head-subtitle">{{ passwordForm.username }}<span v-if="passwordForm.emailId"> · {{ passwordForm.emailId }}</span></div>
        </div>
        <v-card-text class="uf-body">
          <div class="uf-notice">
            <v-icon size="16">mdi-shield-alert-outline</v-icon>
            <span>Setting a new password signs this account out of every active session.</span>
          </div>
          <div class="uf-stack">
            <div class="uf-field">
              <label class="uf-label" for="uf-new-password">New password *</label>
              <v-text-field
                id="uf-new-password"
                v-model="passwordForm.password"
                :type="showResetPassword ? 'text' : 'password'"
                placeholder="Enter new password"
                outlined
                dense
                hide-details
                :append-icon="showResetPassword ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append="showResetPassword = !showResetPassword"
              />
            </div>
            <div class="uf-field">
              <label class="uf-label" for="uf-confirm-password">Confirm new password *</label>
              <v-text-field
                id="uf-confirm-password"
                v-model="passwordForm.confirmPassword"
                :type="showResetPasswordConfirm ? 'text' : 'password'"
                placeholder="Re-enter new password"
                outlined
                dense
                hide-details
                :append-icon="showResetPasswordConfirm ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append="showResetPasswordConfirm = !showResetPasswordConfirm"
              />
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="px-5 pb-5">
          <v-spacer />
          <v-btn depressed class="btn-act btn-act--neutral" @click="passwordDialog = false">Cancel</v-btn>
          <v-btn depressed class="btn-act btn-act--info" :disabled="busy" @click="submitResetPassword">
            <v-icon left small>mdi-lock-reset</v-icon>Update password
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete -->
    <v-dialog v-model="deleteDialog" max-width="460" persistent>
      <v-card class="dialog-card">
        <div class="dialog-head dialog-head--danger">
          <div class="dialog-head-title">Delete user account</div>
          <div class="dialog-head-subtitle">{{ userToDelete ? userToDelete.username : '' }}</div>
        </div>
        <v-card-text class="uf-body">
          <p class="uf-text">
            Permanently delete the account <strong>{{ userToDelete ? userToDelete.username : '' }}</strong>?
          </p>
          <div class="uf-warning">This cannot be undone. All active sessions and tokens of the account are revoked.</div>
        </v-card-text>
        <v-card-actions class="px-5 pb-5">
          <v-spacer />
          <v-btn depressed class="btn-act btn-act--neutral" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn depressed class="btn-act btn-act--danger-solid" :disabled="busy" @click="submitDeleteUser">
            <v-icon left small>mdi-delete-outline</v-icon>Delete account
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { CREATABLE_ROLE_OPTIONS, OFFICIAL_TYPES, DIVISION_STAFF_TYPES, roleName } from "./userRoles";

// Create / edit / reset password / delete dialogs of the super admin's user
// pages. Open them through the ref: openCreate(uType), openEdit(user),
// openPassword(user), openDelete(user). Emits "notify" (message, ok) and
// "saved" (action) after a change goes through.
export default {
  name: "UserFormDialogs",
  props: {
    circles: { type: Array, default: () => [] },
    divisions: { type: Array, default: () => [] },
  },
  data() {
    return {
      busy: false,
      roleItems: CREATABLE_ROLE_OPTIONS.map((r) => ({ value: r.value, text: `${r.text} (uType: ${r.value})` })),
      statusItems: [
        { value: "Active", text: "Active" },
        { value: "NotActive", text: "Inactive" },
      ],

      createDialog: false,
      showCreatePassword: false,
      createForm: {
        username: "",
        officeName: "",
        emailId: "",
        mobNumber: "",
        password: "",
        uType: 772,
        status: "Active",
        circleId: null,
        divisionId: null,
      },

      editDialog: false,
      editForm: {
        _id: null,
        username: "",
        officeName: "",
        emailId: "",
        mobNumber: "",
        code: "",
        uType: 772,
        status: "Active",
        circleId: null,
        divisionId: null,
      },

      passwordDialog: false,
      showResetPassword: false,
      showResetPasswordConfirm: false,
      passwordForm: {
        _id: null,
        username: "",
        emailId: "",
        password: "",
        confirmPassword: "",
      },

      deleteDialog: false,
      userToDelete: null,
    };
  },

  computed: {
    filteredCreateDivisions() {
      return this.divisionsOfCircle(this.createForm.circleId);
    },

    filteredEditDivisions() {
      return this.divisionsOfCircle(this.editForm.circleId);
    },

    createHierarchyPreview() {
      return this.hierarchyPreview(this.createForm);
    },

    editHierarchyPreview() {
      return this.hierarchyPreview(this.editForm);
    },
  },

  methods: {
    notify(message, ok = true) {
      this.$emit("notify", message, ok);
    },

    headers() {
      return { token: localStorage.getItem("token") };
    },

    needsCircle(uType) {
      return OFFICIAL_TYPES.includes(Number(uType));
    },

    needsDivision(uType) {
      return DIVISION_STAFF_TYPES.includes(Number(uType));
    },

    divisionsOfCircle(circleId) {
      if (!circleId) return [];
      const circle = this.circles.find((c) => c._id === circleId);
      if (!circle || !circle.path) return this.divisions;
      return this.divisions.filter((d) => d.path && d.path.startsWith(circle.path));
    },

    hierarchyPreview(form) {
      const t = Number(form.uType);
      const c = this.circles.find((x) => x._id === form.circleId);
      if (t === 774) return c ? `Circle: ${c.officeName}` : null;
      if (DIVISION_STAFF_TYPES.includes(t)) {
        const d = this.divisions.find((x) => x._id === form.divisionId);
        if (c && d) return `${c.officeName} › ${d.officeName}`;
        if (c) return `Circle: ${c.officeName} (select division)`;
      }
      return null;
    },

    // ── Create ──
    openCreate(uType) {
      this.createForm = {
        username: "",
        officeName: "",
        emailId: "",
        mobNumber: "",
        password: "",
        uType: uType ? Number(uType) : 772,
        status: "Active",
        circleId: null,
        divisionId: null,
      };
      this.showCreatePassword = false;
      this.createDialog = true;
    },

    onCreateRoleChange() {
      const t = Number(this.createForm.uType);
      if (!OFFICIAL_TYPES.includes(t)) {
        this.createForm.circleId = null;
        this.createForm.divisionId = null;
      } else if (t === 774) {
        this.createForm.divisionId = null;
      }
    },

    onCreateCircleChange() {
      this.createForm.divisionId = null;
    },

    onCreateDivisionChange() {
      if (this.createForm.divisionId && !this.createForm.officeName) {
        const div = this.divisions.find((d) => d._id === this.createForm.divisionId);
        if (div) {
          this.createForm.officeName = `${div.officeName} - ${roleName(this.createForm.uType)}`;
        }
      }
    },

    submitCreateUser() {
      if (!this.createForm.username || !this.createForm.emailId || !this.createForm.mobNumber || !this.createForm.password) {
        this.notify("Please fill in all required fields.", false);
        return;
      }
      const t = Number(this.createForm.uType);
      if (t === 774 && !this.createForm.circleId) {
        this.notify("Please select a Circle for this Division account.", false);
        return;
      }
      if (DIVISION_STAFF_TYPES.includes(t)) {
        if (!this.createForm.circleId) {
          this.notify("Please select a Circle.", false);
          return;
        }
        if (!this.createForm.divisionId) {
          this.notify("Please select an Assigned Division.", false);
          return;
        }
      }

      this.busy = true;
      axios({ method: "POST", url: "/superadmin/user/create", headers: this.headers(), data: this.createForm })
        .then((res) => {
          this.busy = false;
          if (res.data.status) {
            this.createDialog = false;
            this.notify("User account created successfully!");
            this.$emit("saved", "create");
          } else {
            this.notify(res.data.msg || "Failed to create user.", false);
          }
        })
        .catch((err) => {
          this.busy = false;
          console.error("Create user error:", err);
          this.notify("Server error creating user.", false);
        });
    },

    // ── Edit ──
    openEdit(item) {
      let circleId = null;
      let divisionId = null;

      if (item.path) {
        const div = this.divisions.find((d) => item.path === d.path || item.path.startsWith(d.path));
        if (div) {
          divisionId = div._id;
          const circle = this.circles.find((c) => div.path.startsWith(c.path));
          if (circle) circleId = circle._id;
        } else {
          const circle = this.circles.find((c) => item.path.startsWith(c.path));
          if (circle) circleId = circle._id;
        }
      }

      this.editForm = {
        _id: item._id,
        username: item.username || "",
        officeName: item.officeName || "",
        emailId: item.emailId || "",
        mobNumber: item.mobNumber || "",
        code: item.code && item.code !== "xxx" ? item.code : "",
        uType: item.uType || 774,
        status: item.status || "Active",
        circleId,
        divisionId,
      };
      this.editDialog = true;
    },

    onEditRoleChange() {
      const t = Number(this.editForm.uType);
      if (!OFFICIAL_TYPES.includes(t)) {
        this.editForm.circleId = null;
        this.editForm.divisionId = null;
      } else if (t === 774) {
        this.editForm.divisionId = null;
      }
    },

    onEditCircleChange() {
      if (this.editForm.divisionId && !this.filteredEditDivisions.some((d) => d._id === this.editForm.divisionId)) {
        this.editForm.divisionId = null;
      }
    },

    submitEditUser() {
      if (!this.editForm.username || !this.editForm.emailId || !this.editForm.mobNumber) {
        this.notify("Username, Email, and Mobile are required.", false);
        return;
      }
      const t = Number(this.editForm.uType);
      if (t === 774 && !this.editForm.circleId) {
        this.notify("Please select a Circle for this Division account.", false);
        return;
      }
      if (DIVISION_STAFF_TYPES.includes(t)) {
        if (!this.editForm.circleId) {
          this.notify("Please select a Circle.", false);
          return;
        }
        if (!this.editForm.divisionId) {
          this.notify("Please select an Assigned Division.", false);
          return;
        }
      }

      // The division code is only sent for a DFO and only when one is given;
      // the backend would otherwise reset the code (and the division's
      // eTreasury code) to "xxx".
      const payload = { ...this.editForm };
      const code = (payload.code || "").trim();
      delete payload.code;
      if (t === 774 && code) payload.code = code;

      this.busy = true;
      axios({ method: "PUT", url: "/superadmin/user/update", headers: this.headers(), data: payload })
        .then((res) => {
          this.busy = false;
          if (res.data.status) {
            this.editDialog = false;
            this.notify("User details updated successfully!");
            this.$emit("saved", "edit");
          } else {
            this.notify(res.data.msg || "Failed to update user.", false);
          }
        })
        .catch((err) => {
          this.busy = false;
          console.error("Edit user error:", err);
          this.notify("Server error updating user.", false);
        });
    },

    // ── Reset password ──
    openPassword(item) {
      this.passwordForm = {
        _id: item._id,
        username: item.username,
        emailId: item.emailId,
        password: "",
        confirmPassword: "",
      };
      this.showResetPassword = false;
      this.showResetPasswordConfirm = false;
      this.passwordDialog = true;
    },

    submitResetPassword() {
      if (!this.passwordForm.password || this.passwordForm.password.trim() === "") {
        this.notify("Please enter a new password.", false);
        return;
      }
      if (this.passwordForm.password !== this.passwordForm.confirmPassword) {
        this.notify("Passwords do not match.", false);
        return;
      }

      this.busy = true;
      axios({
        method: "PUT",
        url: "/superadmin/user/reset-password",
        headers: this.headers(),
        data: { _id: this.passwordForm._id, password: this.passwordForm.password },
      })
        .then((res) => {
          this.busy = false;
          if (res.data.status) {
            this.passwordDialog = false;
            this.notify(res.data.msg || "Password reset successfully!");
            this.$emit("saved", "password");
          } else {
            this.notify(res.data.msg || "Failed to reset password.", false);
          }
        })
        .catch((err) => {
          this.busy = false;
          console.error("Password reset error:", err);
          this.notify("Server error resetting password.", false);
        });
    },

    // ── Delete ──
    openDelete(item) {
      this.userToDelete = item;
      this.deleteDialog = true;
    },

    submitDeleteUser() {
      if (!this.userToDelete) return;
      this.busy = true;
      axios({ method: "DELETE", url: `/superadmin/user/${this.userToDelete._id}`, headers: this.headers() })
        .then((res) => {
          this.busy = false;
          this.deleteDialog = false;
          if (res.data.status) {
            this.notify("User account deleted successfully!");
            this.$emit("saved", "delete");
          } else {
            this.notify(res.data.msg || "Failed to delete user.", false);
          }
        })
        .catch((err) => {
          this.busy = false;
          this.deleteDialog = false;
          console.error("Delete user error:", err);
          this.notify("Server error deleting user.", false);
        });
    },
  },
};
</script>

<style scoped>
.uf-body {
  padding: 20px 24px 8px !important;
}

.uf-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.uf-stack {
  display: grid;
  gap: 16px;
}

.uf-field {
  min-width: 0;
}

.uf-field--full {
  grid-column: 1 / -1;
}

.uf-label {
  display: block;
  margin-bottom: 6px;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #64748b;
}

.uf-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
}

.uf-field >>> .v-input__prepend-inner .v-icon {
  color: #94a3b8 !important;
}

.uf-preview,
.uf-notice,
.uf-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.45;
}

.uf-preview .v-icon,
.uf-notice .v-icon {
  margin-top: 1px;
  color: inherit !important;
}

.uf-preview {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
}

.uf-notice {
  margin-bottom: 16px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
}

.uf-warning {
  margin-top: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.uf-text {
  margin: 0;
  font-size: 14px;
  color: #334155;
}

@media (max-width: 600px) {
  .uf-body {
    padding: 16px 18px 4px !important;
  }

  .uf-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }
}
</style>
